import {useCallback, useState} from 'react';
import {LivenessAction} from 'components/LivenessDetection/types';
import * as Sentry from '@sentry/react';
import {Navigate, useParams} from 'react-router-dom';
import {displayError, IV_PHOTO_SOURCE, IV_DOCUMENT_IMAGE_TYPES} from '@guestapp/sdk';
import {blurSelfiePhoto} from 'pages/IdentityVerification/utils';
import {PATHS} from 'Routes';
import {useSummary} from 'context/summary';
import {useDocumentSelfieDetection} from 'hooks';
import {useIVStore} from 'store/identityVerification';
import {LivenessDetection} from 'components/LivenessDetection';

function SelfieLivenessView() {
  const {id} = useParams();
  const {reservationId, countryCode, data: summary} = useSummary();
  const frontPhoto = useIVStore(state => state.imagesDetails.front);
  const livenessResultData = useIVStore(state => state.livenessResultData);
  const setLivenessResultData = useIVStore(state => state.setLivenessResultData);

  const [livenessVideo, setLivenessVideo] = useState<Blob | null>(null);
  const {
    sendSelfieToDetectFace,
    ProcessModal,
    RetryModal,
    ErrorModal,
    isProcessing,
    processState,
  } = useDocumentSelfieDetection({
    docType: IV_DOCUMENT_IMAGE_TYPES.selfie,
    livenessVideo,
  });

  const handleChangePhoto = useCallback(
    async ({
      screenshot,
      performedBy,
      completedActions,
      recordingVideo,
    }: {
      screenshot: string | null;
      recordingVideo: Blob | null;
      performedBy: 'CLIENT' | 'SERVER';
      completedActions: LivenessAction[];
    }) => {
      if (!screenshot) return;

      if (countryCode) {
        try {
          const blurredPhoto = await blurSelfiePhoto({
            photo: screenshot,
          });

          if (blurredPhoto) {
            if (recordingVideo) {
              setLivenessVideo(recordingVideo);
            }
            sendSelfieToDetectFace({
              source: IV_PHOTO_SOURCE.appCamera,
              countryCode,
              reservationId,
              image: blurredPhoto,
            });
            setLivenessResultData({
              ...livenessResultData,
              performedBy,
              livenessPassed: true,
              completedActions,
            });
          }
        } catch (error) {
          displayError(error as string);
        }
      } else {
        console.error('countryCode is missing', summary);
        Sentry.captureMessage('countryCode is missing', {extra: {summary}});
      }
    },
    [
      countryCode,
      reservationId,
      sendSelfieToDetectFace,
      summary,
      setLivenessResultData,
      livenessResultData,
    ],
  );

  if (!frontPhoto.uploaded) {
    return <Navigate to={`../../${PATHS.verification.main}/${id ?? ''}`} />;
  }

  const isCameraVisible = Boolean(
    !isProcessing && processState !== 'error' && processState !== 'success',
  );

  return (
    <>
      {isCameraVisible && <LivenessDetection onComplete={handleChangePhoto} />}
      <ProcessModal />
      <RetryModal />
      <ErrorModal />
    </>
  );
}

export {SelfieLivenessView};
